import React from 'react';
import type { Status } from '@jira/project-settings__project-configuration-commons/src/index.tsx';
import { Text } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { EDGE_TYPE } from '../../../common/constants/index.tsx';
import type { EdgeId, ItemReference, ListEdgeDetails } from '../../../common/types/index.tsx';
import RulesIndicator from '../../../common/ui/rules-indicator/index.tsx';
import ValidationIndicator from '../../../common/ui/validation-indicator/index.tsx';
import messages from './messages.tsx';

export type Props = {
	edge: ListEdgeDetails;
	statuses: Status[];
	selectedItem?: ItemReference;
	isImmutable?: boolean;
	describedBy?: string;
	onFocus?: (id: EdgeId) => void;
	onSelect?: (item: ItemReference | undefined) => void;
};

const ListEdge = ({
	edge,
	statuses,
	isImmutable = false,
	describedBy,
	selectedItem,
	onSelect,
	onFocus,
}: Props) => {
	const isSelected =
		!!selectedItem && selectedItem.type === EDGE_TYPE && selectedItem.id === edge.id;

	const handleClick = () => {
		onSelect?.({ type: 'edge', id: edge.id });
	};

	const handleFocus = () => {
		onFocus?.(edge.id);
	};

	const content = (
		<>
			<EdgeLabel edge={edge} statuses={statuses} /> <RulesIndicator rulesCount={edge.rulesCount} />{' '}
			<ValidationIndicator validationFlag={edge.validationFlag} />
		</>
	);

	if (isImmutable) {
		return <div aria-current={isSelected}>{content}</div>;
	}

	return (
		// We are using a primitive here to support accessible navigation of complex diagram,
		// this will never be visible to the user
		// eslint-disable-next-line @atlaskit/design-system/no-html-button
		<button
			type="button"
			aria-current={isSelected}
			onClick={handleClick}
			onFocus={handleFocus}
			aria-describedby={describedBy}
		>
			{content}
		</button>
	);
};

const EdgeLabel = ({
	edge: { type, transition, parentId },
	statuses,
}: {
	edge: ListEdgeDetails;
	statuses: Status[];
}) => {
	const { formatMessage, formatList } = useIntl();

	const parentStatus = statuses.find((status) => status.id === parentId);

	if (type === 'GLOBAL_LOOPED') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.globalLoopedTransitionLabelIssueTermRefresh
						: messages.globalLoopedTransitionLabel,
					{
						transitionName: transition.name,
					},
				)}
			</Text>
		);
	}

	if (type === 'GLOBAL') {
		return (
			<Text>
				{formatMessage(messages.globalTransitionLabel, {
					parentName: parentStatus?.name,
				})}
			</Text>
		);
	}

	if (type === 'LOOPED') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.loopedTransitionLabelIssueTermRefresh
						: messages.loopedTransitionLabel,
					{
						transitionName: transition.name,
						parentName: parentStatus?.name,
					},
				)}
			</Text>
		);
	}

	const target = statuses.find((status) => status.id === transition.toRef);

	if (type === 'INITIAL') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.initialTransitionLabelIssueTermRefresh
						: messages.initialTransitionLabel,
					{
						transitionName: transition.name,
						targetName: target?.name,
					},
				)}
			</Text>
		);
	}

	const sourceIds = transition.links.map((source) => source.fromRef);

	const otherSourcesNames = statuses
		.filter((status) => status.id !== parentId && sourceIds.includes(status.id))
		.map((status) => status.name);

	return (
		<Text>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.directedTransitionLabelIssueTermRefresh
					: messages.directedTransitionLabel,
				{
					transitionName: transition.name,
					sourceNames: formatList([parentStatus?.name, ...otherSourcesNames]),
					targetName: target?.name,
				},
			)}
		</Text>
	);
};

export default ListEdge;
